<template>
    <article class="bg-gray-50 rounded-lg shadow-sm" :aria-labelledby="headlineId">
        <div
            class="p-2 grid grid-cols-[[link]_auto_[icon]_max-content_[status]_minmax(80px,max-content)_[end]] grid-flow-col items-center gap-12"
        >
            <div class="col-[link] text-sm text-gray-900 flex justify-between font-bold">
                <TextLink :id="headlineId" :to="linkTarget">
                    {{ linkText }}
                </TextLink>
            </div>
            <Tooltip v-if="series.silent" class="col-[icon]">
                <template #trigger="tooltipProps">
                    <NoSoundIcon
                        v-bind="tooltipProps"
                        class="inline w-5 h-5 text-gray-700 cursor-help focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-700 focus-visible:ring-offset-2 rounded-xs"
                        tabindex="0"
                    />
                </template>
                <template #text>{{ $t('external-event-silent-label') }}</template>
            </Tooltip>
            <Tooltip v-else-if="categoryIcon" class="col-[icon]">
                <template #trigger="tooltipProps">
                    <component
                        :is="categoryIcon"
                        class="inline w-5 h-5 text-gray-700 cursor-help focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-700 focus-visible:ring-offset-2 rounded-xs"
                        tabindex="0"
                        v-bind="tooltipProps"
                    />
                </template>
                <template #text>{{
                    $t('external-event-series-category-singular', { category: series.category })
                }}</template>
            </Tooltip>
            <Badge
                class="col-[status] justify-self-end"
                size="Extra-Small"
                :variant="externalEventSeriesStatusToBadgeVariant(series.status)"
            >
                {{ $t('external-event-series-status', { status: series.status }) }}
            </Badge>
        </div>
    </article>
</template>
<script setup lang="ts">
import Badge from '@/components/Badge/Badge.vue';
import ConstructionIcon from '@/components/Icon/ConstructionIcon.vue';
import NoSoundIcon from '@/components/Icon/NoSoundIcon.vue';
import SocietyIcon from '@/components/Icon/SocietyIcon.vue';
import TrophyIcon from '@/components/Icon/TrophyIcon.vue';
import VoteIcon from '@/components/Icon/VoteIcon.vue';
import TextLink from '@/components/TextLink/TextLink.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { ExternalEventSeriesCategory } from '@/generated/graphql';
import { nanoid } from 'nanoid';
import { type Component, computed } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import { externalEventSeriesStatusToBadgeVariant } from '../externalEvent';

const categoryIcons: Partial<Record<ExternalEventSeriesCategory, Component>> = {
    [ExternalEventSeriesCategory.Social]: SocietyIcon,
    [ExternalEventSeriesCategory.Politics]: VoteIcon,
    [ExternalEventSeriesCategory.Sport]: TrophyIcon,
    [ExternalEventSeriesCategory.ExternalConstruction]: ConstructionIcon,
};

type EventSeriesCardProps = {
    series: {
        id: string;
        nameShort: string;
        status: string;
        category: ExternalEventSeriesCategory;
        silent: boolean;
    };
    linkTarget: RouteLocationRaw;
    linkText: string;
    helpText?: string;
    hasBorderBottom?: boolean;
};

const props = defineProps<EventSeriesCardProps>();

const categoryIcon = computed(() => {
    return categoryIcons[props.series.category];
});

const headlineId = nanoid();
</script>
